import { Injectable } from '@angular/core';
import moment, { Moment } from 'moment';
import 'moment-timezone';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { START_OF_DAY } from '../constants';

@Injectable({
  providedIn: 'root',
})
export class DateHelperService {
  // public getDateToAPI(date: Date):

  /**
   * Returns a Date object based on datepicker selection
   * So, convert from `{ day: 6, month: 1, year: 2017 }` to `Thu Jun 01 2017 00:00:00 GMT-0300`
   * @param date
   * @param time If START_OF_DAY, set hour to 0 (start of day). If END_OF_DAY, set hour at 23.59 (end of day)
   * @return Date
   */
  public fromDatePickerToDate(date: NgbDateStruct | string, time: string = null): Date {
    if (date == null) {
      return null;
    }
    let validateDate: NgbDateStruct, d: Date | NgbDateStruct;
    if (typeof date == 'string') {
      const momentDate: Moment = moment(date);
      validateDate = { year: momentDate.year(), month: momentDate.month(), day: momentDate.day() };
      d = new Date(validateDate.year, validateDate.month, validateDate.day);
    } else {
      validateDate = date;
      d = new Date(validateDate.year, validateDate.month - 1, validateDate.day);
    }
    const utcOffset: number = moment().utcOffset() / 60;
    if (time == null || time == START_OF_DAY) d = moment(d).hour(0).minute(0).seconds(0).utcOffset(utcOffset).toDate();
    else d = moment(d).hour(23).minute(59).seconds(59).utcOffset(utcOffset).toDate();

    return d;
  }

  /**
   * Return date formatted to be sent to API. It is based on Date object
   * From date `` to `getFromDateToApi to string `2017-06-01T03:00:42`
   * @param date
   * @param time
   * @return string
   */
  public fromDateToApi(date: Date, time: string = null): string {
    if (date) return this.fromDateToUTCString(date, time);
    else return null;
  }

  /**
   * Convert Date object to ISO string used in API (2017-04-01T00:00:00Z)
   * @param date
   * @param time
   */
  public fromDateToUTCString(date: Date, time: string = null): string {
    // 2017-04-01T00:00:00Z
    const year: number = date.getFullYear();
    const month: number = date.getMonth() + 1;
    let strMonth: string = `${month}`;
    if (month < 10) strMonth = '0' + month;
    const day: number = date.getDate();
    let strDay: string = `${day}`;
    if (day < 10) strDay = '0' + day;

    let formatted: string = '';
    if (time == null || time == START_OF_DAY) formatted = `${year}-${strMonth}-${strDay}T00:00:00Z`;
    else formatted = `${year}-${strMonth}-${strDay}T23:59:59Z`;

    return formatted;
  }

  /**
   * Return Date object from timestamp
   * @param timestamp
   * @param utcOffset
   */
  public fromTimestampToDate(timestamp: number, utcOffset: number = 0): Date {
    if (timestamp) {
      const m: Moment = moment.tz(timestamp, 'UTC');
      if (utcOffset > 0) {
        m.add(utcOffset, 'hours');
      }
      const days: number = m.date();
      const months: number = m.month();
      const years: number = m.year();
      const hours: number = m.hours();
      const minutes: number = m.minutes();
      const seconds: number = m.seconds();

      return new Date(years, months, days, hours, minutes, seconds);
    }

    return null;
  }

  /**
   * Convert from Date object to datepicker format (json)
   * @param date
   * @return json
   */
  public fromDateToDatepicker(date: Date): any {
    if (this.isValidDate(date)) return { day: date.getDate(), month: date.getMonth() + 1, year: date.getFullYear() };
    return null;
  }

  /**
   * Format timezone names from moments
   * @param tzName
   * @return string
   */
  public formatTimezoneName(tzName: string): string {
    const tzNameFilterArray: Array<string> = new Array<string>();

    const tzNameArray: Array<string> = tzName.split('');
    tzNameArray.forEach((c: string) => {
      if (c == '/') c = ' / ';
      else if (c == '_') c = ' ';
      tzNameFilterArray.push(c);
    });

    return tzNameFilterArray.join('');
  }

  /**
   * Returns a NgbDateStruct from a date string
   * @param dateString
   */
  public getFormattedDate(dateString: string): NgbDateStruct | null {
    if (!dateString) {
      return null;
    }

    const date: Date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return null;
    }

    return {
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
  }

  /**
   * Check if value is a valid date
   * @param value
   */
  public isValidDate(value: Date): boolean {
    let valid: boolean = true;
    if (Object.prototype.toString.call(value) === '[object Date]') {
      // It is a date
      valid = !isNaN(value.getTime());
    } else valid = false;
    return valid;
  }

  public addMinutesToDate(date: Date, minutes: number): Date {
    return new Date(date.getTime() + minutes * 60000);
  }

  /**
   * Retrieve date formatted for API
   * @param date
   */
  public formatDate(date: Date): string {
    return moment(date).utcOffset(0).format();
  }

  /**
   * Returns a Date object based on datepicker selection
   * example: { day: 20, month: 1, year: 2017 }
   * @param date
   */
  public getDateFromDatepicker(date: any): Date {
    const d: Date = new Date();
    d.setMonth(date.month - 1);
    d.setFullYear(date.year);
    d.setDate(date.day);
    d.setHours(0, 0, 0, 0); // Normalize time part
    return d;
  }

  /**
   * Gets the user's preferred date format from localStorage settings
   * @returns Date format string or default format if not set
   */
  public getUserDateFormat(): string {
    try {
      const userSettings: string = localStorage.getItem('auth_user_settings');
      if (userSettings) {
        const settings: Record<string, string> = JSON.parse(userSettings);
        return settings.dateFormat || 'MM/dd/yyyy';
      }
    } catch {
      return 'MM/dd/yyyy';
    }
    return 'MM/dd/yyyy'; // Default format
  }

  /**
   * Formats a date according to user preferences using moment.js
   * @param dateValue Date value to format
   * @returns Formatted date string
   */
  public formatDateWithUserPreference(dateValue: string | Date | number | null | undefined): string {
    if (!dateValue) return '-';

    try {
      const date: Moment = moment(dateValue);
      if (!date.isValid()) return '-';
      const format: string = this.getUserDateFormat();
      return date.format(format);
    } catch {
      return '-';
    }
  }

  public getFullCalendarLocale(language: string): string {
    switch (language) {
      case 'zh':
        return 'zh-cn';
      case 'en_us':
        return 'en';
      default:
        return language;
    }
  }

  public convertDate(date: string | number | Date): NgbDateStruct {
    const d: Date = new Date(date);
    return {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
    };
  }

  public toDateObject(dateObj: { day: number; month: number; year: number }): Date {
    const { day, month, year } = dateObj;
    // Note: month is 0-indexed in Date constructor (month - 1)
    return new Date(year, month - 1, day);
  }

  public isDateInRange(
    inputDate: { day: number; month: number; year: number },
    minDate: { day: number; month: number; year: number },
    maxDate: { day: number; month: number; year: number }
  ): boolean {
    const input: Date = this.toDateObject(inputDate);
    const min: Date = this.toDateObject(minDate);
    const max: Date = this.toDateObject(maxDate);
    return input.getTime() >= min.getTime() && input.getTime() <= max.getTime();
  }

  public isStartDateLessThanEndDate(startDate: NgbDateStruct | string, endDate: NgbDateStruct | string): boolean {
    if (!startDate || startDate == '' || !endDate || endDate == '') return false;
    const timestampStartDate: Date = this.fromDatePickerToDate(startDate);
    const timestampEndDate: Date = this.fromDatePickerToDate(endDate);
    return timestampStartDate < timestampEndDate;
  }
}
